import { runWithErrorReporting } from "$root/api/error-reporting/report";
import { useApiGen } from "$root/api/hooks";
import { WidgetDownloadTrigger } from "$root/components/DropdownMenuTrigger";
import type { Instrument } from "$root/functional-areas/instruments-editor/const";
import { buildCompositionExportConfig } from "$root/functional-areas/instruments/export";
import InstrumentTagClassificationSelect from "$root/functional-areas/instruments/instrument-classification/InstrumentTagClassificationSelect";
import { createInstrumentList, instrumentsRepository } from "$root/functional-areas/instruments/repository";
import { markCashRows } from "$root/functional-areas/instruments-editor/quantity/cashRow";
import { userClassifications$ } from "$root/functional-areas/instruments/user-classifications";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import { isAsOfReadable } from "../as-of/useAsOfSnapshot";
import { workspaceRepository } from "$root/functional-areas/workspace/repository";
import { useDecimalPlaces } from "$root/functional-areas/user/useDecimalPlaces";
import { persistedColStateKeys } from "$root/functional-areas/user/userStorage";
import { AgGridWithModules } from "$root/third-party-integrations/ag-grid/ag-grid";
import { trackMixPanelEvent } from "$root/third-party-integrations/initMixPanel";
import { useMappedQueries } from "$root/third-party-integrations/react-query/hooks";
import { columnsDefinition } from "$root/ui-lib/table/column";
import { agExtra } from "$root/ui-lib/table/default-configuration";
import { downloadContentDisposition, preventSubmitOnPressEnter, qualifier } from "$root/utils";
import { InvestmentEnhancementExportControllerApiFactory, type InvestmentSummary } from "@eu.mdotm.api/sphere";
import { DebouncedSearchInput } from "@mdotm/shadcn/legacy/components";
import { useSearchable } from "@mdotm/shadcn/legacy/headless";
import { useAtomValue } from "@mdotm/shadcn/legacy/reactivity";
import { noop } from "@mdotm/shadcn/legacy/utils";
import {
	DropdownMenu,
	StepperInput,
	type DropdownDropdownItem,
	Switch,
	FormField,
	Label,
} from "@mdotm/shadcn/mdotm/components";
import { useQuery } from "@tanstack/react-query";
import type { AgGridReact } from "ag-grid-react";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PortfolioQueryWidgetBase, portfolioWidgetMissingDataReason, WidgetStatus } from "../PortfolioWidgetStatus";
import { PortfolioContext } from "$root/widgets-architecture/contexts/portfolio";

type PortfolioCompositionProps = {
	portfolio: InvestmentSummary;
	enhanced: boolean;
};

type DecimalsLabelProps = {
	htmlFor: string;
};

const DecimalsLabel = ({ htmlFor }: DecimalsLabelProps): React.JSX.Element => {
	return (
		<Label htmlFor={htmlFor} className="mb-px">
			Decimals
		</Label>
	);
};
export const PortfolioComposition = (props: PortfolioCompositionProps): React.JSX.Element => {
	const { enhanced, portfolio } = props;
	const { asOf, asOfSnapshot } = useContext(PortfolioContext);
	const uuid = props.portfolio?.uuid;
	const classifications = useAtomValue(userClassifications$);

	const exportEnhancedApi = useApiGen(InvestmentEnhancementExportControllerApiFactory);

	const { t } = useTranslation();

	const querySummary = portfolioRepository.summary.useQuery(uuid!, { enhanced: false });

	const { currentlyContainsNestedPortfolios } = querySummary.data ?? {};
	// View selector for nested portfolios. `false` (default) = the *nested* view: the portfolio's
	// direct holdings with nested portfolios shown as rows — the exact composition, just like a
	// non-nested portfolio. `true` = the *aggregate* view, which looks through the nested portfolios
	// to their underlying instruments. Non-nested portfolios only ever have the nested/exact view.
	const [showAggregateView, setShowAggregateView] = useState(false);

	const [agGridEl, setAgGridEl] = useState<AgGridReact<Instrument> | null>(null);
	const [groupByClassificationUuid, setGroupByClassificationUuid] = agExtra.useGroupByClassification(agGridEl);

	// Quantity columns (MDOT-24700): the backend provides quantity/price/marketValue on the
	// composition rows — the columns only render them.
	const quantityMode = props.portfolio.quantityMode;

	// Exactly one of the four variants (proposal/plain × quantity/weight) is built. The plain
	// variants carry no proposal context: previous weight and delta belong to the proposal
	// review only, so the non-proposal view must never show them.
	const columns = useMemo(() => {
		if (enhanced) {
			return quantityMode
				? columnsDefinition.instrumentQuantityProposalComposition(classifications, "read", {
						proposalDate: props.portfolio.modificationDate,
						currency: props.portfolio.baseCurrency,
						// The plain summary: on the proposal view `props.portfolio` is the enhanced one, whose
						// action is the proposal's own and never says a conversion is pending.
						summary: querySummary.data,
					})
				: columnsDefinition.instrumentProposalComposition(classifications, "read", {
						proposalDate: props.portfolio.modificationDate,
					});
		}
		return quantityMode
			? columnsDefinition.instrumentQuantityComposition(classifications, "read", {
					currency: props.portfolio.baseCurrency,
				})
			: columnsDefinition.instrumentComposition(classifications, "read");
	}, [enhanced, quantityMode, classifications, props.portfolio, querySummary.data]);

	const queryRaw = useQuery({
		...portfolioRepository.composition.getQueryConfig(uuid ?? "", {
			currentlyContainsNestedPortfolios: currentlyContainsNestedPortfolios ?? false,
			// Repository inverse flag: `false` selects the aggregate (look-through) composition,
			// `true` keeps the direct/nested composition.
			flattenNestedPortfolios: !showAggregateView,
			enhanced,
			asOf,
		}),
		enabled: currentlyContainsNestedPortfolios !== undefined && isAsOfReadable(asOf, asOfSnapshot),
	});

	const userInstrumentsMapQuery = useQuery({
		...instrumentsRepository.userClassifications.getQueryConfig(
			queryRaw.data?.composition?.map((entry) => entry.ticker).filter((ticker) => ticker != null) ?? [],
		),
		enabled: queryRaw.data != null,
	});

	const query = useMappedQueries(
		([{ composition }, userInstrumentsMap]) => {
			if (!uuid) {
				return {
					data: undefined,
					widgetStatus: portfolioWidgetMissingDataReason(props.portfolio, "Composition"),
				};
			}

			if (composition && composition.length > 0) {
				return {
					data: { composition: markCashRows(createInstrumentList({ composition, userInstrumentsMap })) },
					widgetStatus: WidgetStatus.READY,
				};
			}

			return {
				data: undefined,
				widgetStatus: portfolioWidgetMissingDataReason(props.portfolio, "Composition"),
			};
		},
		[props.portfolio, uuid],
		[queryRaw, userInstrumentsMapQuery],
	);

	const [searchInstrument, setSearchInstrument] = useState("");
	const { filtered } = useSearchable({
		collection: query?.data?.data?.composition ?? [],
		query: searchInstrument,
		extractFn: (instrument) =>
			`${instrument.name}${instrument.identifiers?.ALIAS}${instrument.identifiers?.ISIN}${instrument.identifiers?.CUSIP}${instrument.identifiers?.BLOOMBERG_TICKER}`.toLowerCase(),
	});

	const exportPortfolioComposition = useCallback(
		async (downloadTarget: "composition" | "trades") => {
			const trackComposition = () => {
				trackMixPanelEvent(
					enhanced
						? "portfolio.entityAction.downloadProposalComposition"
						: "portfolio.entityAction.downloadComposition",
					{
						portfolio: { uuid: portfolio.uuid!, name: portfolio.name! },
						format: "SPHERE_TEMPLATE_CONVERTER",
					},
				);
			};

			// Trades only exist for enhanced proposals and have no smart-workspace data provider yet,
			// so they stay on the legacy trades-only export.
			if (downloadTarget === "trades") {
				const response = await exportEnhancedApi.exportEnhancedComposition(portfolio.uuid!, "TRADES_ONLY", {
					responseType: "blob",
				});
				await downloadContentDisposition(response);
				trackMixPanelEvent("portfolio.entityAction.downloadProposalTrades", {
					portfolio: { uuid: portfolio.uuid!, name: portfolio.name! },
				});
				return;
			}

			// Both the aggregate view (look-through of nested portfolios, only exposed by nested
			// portfolios) and the nested/exact view mirror the visible table, so both export through the
			// smart-workspace data provider driven by the table's persisted column preferences — the
			// composition flavour is selected via `compositionType`.
			const isAggregateView = (currentlyContainsNestedPortfolios ?? false) && showAggregateView;
			const config = buildCompositionExportConfig("INVESTMENT", {
				investmentUuid: portfolio.uuid!,
				snapshot: enhanced ? "PROPOSAL" : "CURRENT",
				colStateKey: enhanced
					? persistedColStateKeys["tableV3-investment-proposalComposition-read"]
					: persistedColStateKeys["tableV3-investment-composition-read"],
				compositionType: isAggregateView ? "AGGREGATE" : "STANDARD",
				proposalDate: portfolio.modificationDate,
				quantityMode,
				currency: portfolio.baseCurrency,
				asOf,
			});
			await runWithErrorReporting(
				async () => {
					const file = await workspaceRepository.exportWorkspaceDataRaw.fetchQuery(config);
					await downloadContentDisposition(file);
					trackComposition();
				},
				{
					area: "portfolio",
					attemptedOperation: {
						message: "export portfolio composition",
						payload: JSON.stringify(config),
					},
				},
			).catch(noop);
		},
		[enhanced, exportEnhancedApi, portfolio, currentlyContainsNestedPortfolios, showAggregateView, quantityMode, asOf],
	);

	const dropdownMenuActions = useMemo<DropdownDropdownItem[]>(
		() => [
			{
				icon: "xls",
				onClickAsync: () => exportPortfolioComposition("composition"),
				label: t("COMPOSITION.DOWNLOAD_TITLE"),
				"data-qualifier": qualifier.component.dropdown.item("Composition"),
			},
			...(portfolio?.status === "PROPOSAL_READY" && enhanced
				? [
						{
							icon: "xls" as const,
							onClickAsync: () => exportPortfolioComposition("trades"),
							label: t("COMPOSITION.DOWNLOAD_TRADES_TITLE"),
							"data-qualifier": qualifier.component.dropdown.item("Trades"),
						},
					]
				: []),
		],
		[enhanced, exportPortfolioComposition, portfolio?.status, t],
	);

	const persistColProps = agExtra.usePersistColState(
		enhanced
			? quantityMode
				? persistedColStateKeys["tableV5-investment-quantityProposalComposition-read"]
				: persistedColStateKeys["tableV3-investment-proposalComposition-read"]
			: quantityMode
				? persistedColStateKeys["tableV4-investment-quantityComposition-read"]
				: persistedColStateKeys["tableV3-investment-composition-read"],
	);
	const { userDecimalPlacesPreferences, customerDecimalPlacesPreferences, handlePrecisionChange } = useDecimalPlaces();

	// biome-ignore lint/correctness/useExhaustiveDependencies: userDecimalPlacesPreferences is a trigger-only dependency — AG Grid must refresh after the state commit
	useEffect(() => {
		const cols = enhanced ? ["WEIGHT", "PREVIOUS_WEIGHT", "ENHANCED_WEIGHT", "DELTA_WEIGHT"] : ["WEIGHT"];
		agGridEl?.api?.refreshCells({ force: true, columns: cols });
	}, [userDecimalPlacesPreferences, agGridEl, enhanced]);

	return (
		<div className="rounded-sm bg-white min-h-0 flex flex-col p-4 h-[calc(100dvh-180px)] ">
			<PortfolioQueryWidgetBase query={query}>
				{() => (
					<>
						<div className="flex justify-between mb-6">
							<DebouncedSearchInput
								externalQuery={searchInstrument}
								onChange={setSearchInstrument}
								name="search"
								maxLength={60}
								placeholder={t("PORTFOLIOS.CONSTRAINTS_TARGETS.PORTFOLIO_LIST")}
								onKeyDown={preventSubmitOnPressEnter}
								style={{ width: 692 }}
							/>
							<div className="flex space-x-4 items-end">
								<FormField label={DecimalsLabel}>
									{(props) => (
										<StepperInput
											{...props}
											size="x-small"
											value={userDecimalPlacesPreferences ?? null}
											onChange={handlePrecisionChange}
											max={customerDecimalPlacesPreferences}
											min={2}
										/>
									)}
								</FormField>
								<InstrumentTagClassificationSelect
									value={groupByClassificationUuid ?? null}
									onChange={setGroupByClassificationUuid}
								/>
								{currentlyContainsNestedPortfolios && (
									<Switch checked={showAggregateView} onChange={() => setShowAggregateView((prev) => !prev)}>
										Expand nested portfolios
									</Switch>
								)}
								<DropdownMenu
									Trigger={WidgetDownloadTrigger}
									extraTriggerProps={{ "data-qualifier": qualifier.component.dropdown.trigger("Download") }}
									actions={dropdownMenuActions}
								/>
							</div>
						</div>
						<div className="min-h-0 grow flex flex-col">
							<AgGridWithModules<Instrument>
								{...agExtra.aggFunctions()}
								{...persistColProps}
								innerRef={setAgGridEl}
								columnDefs={columns}
								rowData={filtered}
								getRowId={(x) => x.data.tickerName ?? x.data.id}
							/>
						</div>
					</>
				)}
			</PortfolioQueryWidgetBase>
		</div>
	);
};
